import { useQuery } from '@tanstack/react-query';
import AppAuth from 'AppAuth';
import AppStore from 'fetch/AppFetch';
import { USER_TYPE_ZYWAVE } from 'lib/config/appConstants';

/**
 * Custom hook to manage Zywave feature licenses and agencies.
 *
 * This hook initializes a query to fetch Zywave feature license agencies
 * based on the provided parameters and listens for changes in localStorage
 * to trigger a refetch of the data.
 *
 * @param userId - Optional user identifier used as part of the query key.
 * @param accountType - Optional account type used to determine if Zywave features are enabled.
 *
 * @returns The result of the query, including data, status, and refetch functionality.
 *
 * @remarks
 * - The query is enabled when `userId` is provided and `accountType` matches `USER_TYPE_ZYWAVE`.
 * - The query data is cached and considered stale after 10 minutes.
 */
export function useZywaveFeatures(userId?: string, accountType?: number) {
  // Enable the query when we have user data and it's a Zywave user
  const isEnabled = !!userId && accountType === USER_TYPE_ZYWAVE;

  const queryResult = useQuery({
    queryKey: ['zywaveFeatures', userId ?? ''],
    queryFn: () => AppAuth.getZywaveFeatureLicenseAgencies(),
    enabled: isEnabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  // if account type is not zywave, clear out any stored user features
  if (accountType !== USER_TYPE_ZYWAVE) {
    AppStore.deleteZywaveUserFeatureAgencies();
    return undefined;
  }
  AppStore.storeZywaveUserFeatureAgencies(queryResult?.data || {});
  return queryResult;
}
